import { useEffect, useRef, useState } from "react";
import { Award, Users, Calendar, CheckCircle, MapPin } from "lucide-react";

const TEAM_IMAGE = "https://images.unsplash.com/photo-1771219491795-3b4dafc1cdf3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjB3YXJtJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcyMDg3MjMxfDA&ixlib=rb-4.1.0&q=85";

const stats = [
  { icon: CheckCircle, value: 200, suffix: "+", label: "Projects Completed" },
  { icon: Calendar, value: 10, suffix: "+", label: "Years of Experience" },
];

const locations = [
  "Bangalore", "Bidar", "Gulbarga", "Sedam", "Hassan",
  "Mangalore", "Hyderabad", "Tumkur", "Udupi",
];

function AnimatedCounter({ target, suffix, started }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!started) return;
    let frame = 0;
    const totalFrames = 60;
    const increment = target / totalFrames;
    const timer = setInterval(() => {
      frame++;
      setCount(Math.min(Math.round(increment * frame), target));
      if (frame >= totalFrames) clearInterval(timer);
    }, 25);
    return () => clearInterval(timer);
  }, [target, started]);

  return (
    <span className="font-heading text-3xl md:text-4xl font-bold text-charcoal tabular-nums">
      {count}{suffix}
    </span>
  );
}

export default function About() {
  const sectionRef = useRef(null);
  const [countersStarted, setCountersStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll(".animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right");
            elements.forEach((el) => el.classList.add("visible"));
            setCountersStarted(true);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      data-testid="about-section"
      className="py-24 md:py-32 bg-[#FDFDFC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-20 items-start">
          {/* Image */}
          <div className="animate-on-scroll-left relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={TEAM_IMAGE}
                alt="Giri Interiors luxury space"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            {/* Accent border */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-gold -z-10 hidden md:block" />
          </div>

          {/* Content */}
          <div className="animate-on-scroll-right">
            <div className="w-12 h-[2px] bg-gold mb-6" />
            <h2
              data-testid="about-heading"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-charcoal tracking-[-0.02em] leading-tight"
            >
              About Giri Interiors and Constructions
            </h2>

            <p
              data-testid="about-description"
              className="font-body text-base text-[#555555] mt-8 leading-relaxed font-light"
            >
              With years of experience in construction and interior design, Giri
              Interiors and Constructions delivers quality craftsmanship and
              innovative designs. From concept to completion, we bring your vision
              to life with precision and passion.
            </p>

            <p className="font-body text-base text-[#555555] mt-4 leading-relaxed font-light">
              Based in Bangalore, we have earned the trust of homeowners and
              businesses alike through our unwavering commitment to excellence and
              attention to every detail.
            </p>

            {/* Stats */}
            <div
              data-testid="about-stats"
              className="grid grid-cols-2 gap-8 mt-12 pt-12 border-t border-[#E2E2E0]"
            >
              {stats.map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.label} data-testid={`stat-${i}`} className="flex flex-col gap-2">
                    <Icon size={20} className="text-gold" strokeWidth={1.5} />
                    <AnimatedCounter
                      target={stat.value}
                      suffix={stat.suffix}
                      started={countersStarted}
                    />
                    <span className="font-body text-sm text-[#666666] font-medium">
                      {stat.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Locations */}
            <div className="mt-12 pt-12 border-t border-[#E2E2E0]" data-testid="about-locations">
              <div className="flex items-center gap-2 mb-6">
                <MapPin size={20} className="text-gold" strokeWidth={1.5} />
                <h3 className="font-heading text-lg md:text-xl font-semibold text-charcoal tracking-[-0.01em]">
                  Our Presence Across India
                </h3>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {locations.map((city) => (
                  <span
                    key={city}
                    data-testid={`location-${city.toLowerCase()}`}
                    className="font-body text-sm font-medium px-4 py-2 border border-[#E2E2E0] text-[#555555] bg-[#F2F2F0] hover:border-gold hover:text-gold transition-all duration-300 cursor-default"
                  >
                    {city}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
